/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { GraphicalTakeOffPoint } from '@gh/builder-shared-data';
import { isNotNil } from '@gh/core-util';
import { clamp, noop, values } from 'lodash';
import { Color, Group, Item, Path, Point, Raster } from 'paper';
import {
  getGraphicalTakeOffColor,
  getGraphicalTakeOffColorPalette,
  getGraphicalTakeOffStyle,
  GraphicalTakeOffColorPalette, GraphicalTakeOffSize,
  GraphicalTakeOffTopView,
  GraphicalTakeOffViewTransform, newPoint,
} from '../../resources/models';
import { PaperElementEffectFn } from './graphical-take-off-element';

export const GTO_MEASUREMENT_STROKE_WIDTH: number = 3;
export const GTO_MEASUREMENT_SELECTION_WIDTH: number = 1;
const CANVAS_2D_WILL_READ_FREQUENTLY: CanvasRenderingContext2DSettings = { willReadFrequently: true };

export function hexToRgbaColor(hexColor: any): Color {
  const colorRadix8 = 8;
  const colorRadix16 = 16;
  const colorRadix255 = 0xFF;
  const colorOpacity = 1;

  hexColor = parseInt(hexColor.slice(1), colorRadix16);

  const r = (hexColor >> colorRadix16) & colorRadix255;
  const g = (hexColor >> colorRadix8) & colorRadix255;
  const b = hexColor & colorRadix255;

  return new Color(r, g, b, colorOpacity);
}

export const toPaperPoint = (point: GraphicalTakeOffPoint) => new Point(point.x, point.y);
export const fromPaperPoint = (point: Point) => ({ x: point.x, y: point.y });

export const isPointInBox = (point: GraphicalTakeOffPoint, box: GraphicalTakeOffSize) =>
  point.x >= 0 && point.x <= box.width && point.y >= 0 && point.y <= box.height;

export const constraintPointByBox = (point: GraphicalTakeOffPoint, box: GraphicalTakeOffSize) =>
  newPoint(clamp(point.x, 0, box.width), clamp(point.y, 0, box.height));

export const visiblePaperEffect = (item: Item, [visible]: [boolean]) => item.visible = visible;
export const constantScalePaperEffect = (item: Item,
                                         [transform]: [GraphicalTakeOffViewTransform],
                                         [previousTransform]: [GraphicalTakeOffViewTransform]) => {
  if (isNotNil(previousTransform)) {
    item.scale(1 / previousTransform.scale);
  }
  item.scale(transform ? transform.scale : 1);
};
export const constantScaleStrokeWidthPaperEffect = (item: Item,
                                                    [transform]: [GraphicalTakeOffViewTransform],
                                                    [previousTransform]: [GraphicalTakeOffViewTransform]) => {
  if (isNotNil(previousTransform)) {
    item.strokeWidth = item.strokeWidth / previousTransform.scale;
  }
  item.strokeWidth = item.strokeWidth * transform.scale;
};

export const composePaperEffects = (...effects: PaperElementEffectFn[]): PaperElementEffectFn =>
  (item, current, previous) => effects.forEach((effect) => effect(item, current, previous));

export const getClosestOfType = (item: Item, type: string): Maybe<Item> => {
  let current: Maybe<Item> = item;

  while (current) {
    if (current.data.type === type) {
      return current;
    }
    current = current.parent;
  }
};

export const getClosestWithType = (item: Item): Maybe<Item> => {
  let current: Maybe<Item> = item;

  while (current) {
    if (current.data.type) {
      return current;
    }
    current = current.parent;
  }
};

export const getItemType = (item: Item) => item.data.type;

const GTO_TOP_STYLES = [
  '',
  'solid',
  'clear',
  'horizontal',
  'vertical',
  'diagonalRtl',
  'diagonalLtr',
  'cross',
  'diagCross',
];

const GTO_FILLING_ALGORITHMS = {
  '': noop,
  clear: noop,
  cross(patternCtx: CanvasRenderingContext2D, patternSize: number,
        color: GraphicalTakeOffColorPalette): void {
    patternCtx.strokeStyle = color.primary;

    patternCtx.moveTo(0, 0);
    patternCtx.lineTo(patternSize, 0);
    patternCtx.stroke();

    patternCtx.moveTo(0, 0);
    patternCtx.lineTo(0, patternSize);
    patternCtx.stroke();
  },
  diagCross(patternCtx: CanvasRenderingContext2D, patternSize: number,
                       color: GraphicalTakeOffColorPalette): void {
    patternCtx.strokeStyle = color.primary;

    patternCtx.moveTo(0, 0);
    patternCtx.lineTo(patternSize, patternSize);
    patternCtx.stroke();

    patternCtx.moveTo(0, patternSize);
    patternCtx.lineTo(patternSize, 0);
    patternCtx.stroke();
  },
  vertical(patternCtx: CanvasRenderingContext2D, patternSize: number,
                      color: GraphicalTakeOffColorPalette): void {
    patternCtx.strokeStyle = color.primary;

    patternCtx.moveTo(0, 0);
    patternCtx.lineTo(0, patternSize);
    patternCtx.stroke();
  },
  horizontal(patternCtx: CanvasRenderingContext2D, patternSize: number,
                        color: GraphicalTakeOffColorPalette): void {
    patternCtx.strokeStyle = color.primary;

    patternCtx.moveTo(0, 0);
    patternCtx.lineTo(patternSize, 0);
    patternCtx.stroke();
  },
  diagonalRtl(patternCtx: CanvasRenderingContext2D, patternSize: number,
                         color: GraphicalTakeOffColorPalette): void {
    patternCtx.strokeStyle = color.primary;

    patternCtx.moveTo(0, patternSize);
    patternCtx.lineTo(patternSize, 0);
    patternCtx.stroke();
  },
  diagonalLtr(patternCtx: CanvasRenderingContext2D, patternSize: number,
                         color: GraphicalTakeOffColorPalette): void {
    patternCtx.strokeStyle = color.primary;

    patternCtx.moveTo(0, 0);
    patternCtx.lineTo(patternSize, patternSize);
    patternCtx.stroke();
  },
  solid(patternCtx: CanvasRenderingContext2D, patternSize: number,
                   color: GraphicalTakeOffColorPalette): void {
    const alpha = 0.4;

    patternCtx.globalAlpha = alpha;
    patternCtx.fillStyle = color.primary;
    patternCtx.fillRect(0, 0, patternSize, patternSize);
  },
};

export const createFilling = (top: GraphicalTakeOffTopView, points: GraphicalTakeOffPoint[]): Item => {
  const paramStyle = GTO_TOP_STYLES[getGraphicalTakeOffStyle(top)];
  const palette = getGraphicalTakeOffColorPalette(getGraphicalTakeOffColor(top));
  const maskPath = new Path(points.map(toPaperPoint));

  const patternSize = 6;
  const patternCanvas = document.createElement('canvas');
  patternCanvas.width = patternSize;
  patternCanvas.height = patternSize;

  const fillCanvas = document.createElement('canvas');
  fillCanvas.width = maskPath.bounds.width;
  fillCanvas.height = maskPath.bounds.height;

  const patternCtx = patternCanvas.getContext('2d', CANVAS_2D_WILL_READ_FREQUENTLY)!;
  const fillCtx = fillCanvas.getContext('2d', CANVAS_2D_WILL_READ_FREQUENTLY)!;
  if (GTO_FILLING_ALGORITHMS[paramStyle]) {
    GTO_FILLING_ALGORITHMS[paramStyle](patternCtx, patternSize, palette);
  }

  fillCtx.fillStyle = <CanvasPattern>fillCtx.createPattern(patternCanvas, 'repeat');
  fillCtx.fillRect(0, 0, maskPath.bounds.width, maskPath.bounds.height);
  fillCtx.fill();

  const raster = new Raster(fillCanvas, new Point(maskPath.bounds.x, maskPath.bounds.y));
  raster.fitBounds(maskPath.bounds, true);
  maskPath.clipMask = true;

  return new Group([raster, maskPath]);
};

const Z_LEVEL_INDEX: Hash<{ front: Item; back: Item }> = {};

export const setupZLevels = (zLevels: string[]) => {
  zLevels.forEach((name) => {
    const back = new Group();
    const front = new Group();
    Z_LEVEL_INDEX[name] = { back, front };
  });
};

export const cleanZLevels = () => values(Z_LEVEL_INDEX).forEach(({ front, back }) => {
  front.remove();
  back.remove();
});

export const moveOnFront = (item: Item, name: string) => item.moveBelow(Z_LEVEL_INDEX[name].front);
export const moveOnBack = (item: Item, name: string) => item.moveBelow(Z_LEVEL_INDEX[name].back);

export const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve) => {
    if (canvas.toBlob) {
      canvas.toBlob(<any>resolve, 'image/png');
    } else {
      setTimeout(() => {
        resolve(canvasToBlobPolyfill(canvas, 'image/png'));
      });
    }
  });

export const canvasToBlobPolyfill = (canvas: HTMLCanvasElement, mimeType: string): Blob => {
  const dataUrl = canvas.toDataURL(mimeType).split(',')[1];

  const bStr = atob(dataUrl);

  let n = bStr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bStr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mimeType });
};
